import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ChevronRight, CalendarDays, Clock, GraduationCap, Bell, Pencil, Building2, Users,
  CheckCircle2, Award, Briefcase, ArrowRight, Lock, Loader2,
} from 'lucide-react';
import {
  Metric, Spinner, STAGE_PILL, PROGRAM_PILL, PROGRAM_STATUS, attentionMeta,
  YOUR_ACTION_KINDS, formatDayYear,
} from './shared';
import CompaniesOffersTab from './CompaniesOffersTab';
import InternshipApplicationsTab from '../InternshipApplicationsTab';
import InternshipExecutionTab from '../InternshipExecutionTab';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'companies', label: 'Companies & Offers' },
  { id: 'applications', label: 'Applications' },
  { id: 'placements', label: 'Placements' },
];

/** Everything for one programme: its progress, companies, applications and placements. */
export default function ProgramWorkspace({ program, loading, tab, focus, setTab, onBack, onRefresh, onEdit }) {
  const [closing, setClosing] = useState(false);
  const [closingApps, setClosingApps] = useState(false);
  const [reopeningApps, setReopeningApps] = useState(false);
  if (loading && !program) return <Spinner />;
  if (!program) {
    return (
      <div className="text-center py-12" data-testid="program-missing">
        <p className="text-sm text-[#9CA3AF] mb-3">This programme is no longer available.</p>
        <Button size="sm" variant="outline" onClick={onBack} className="rounded-lg text-xs border-gray-200" data-testid="program-missing-back">
          Back to Programs
        </Button>
      </div>
    );
  }

  const m = program.metrics;
  const attention = Object.entries(program.attention || {});
  const mine = attention.filter(([k]) => YOUR_ACTION_KINDS.includes(k));
  const waiting = attention.filter(([k]) => !YOUR_ACTION_KINDS.includes(k));

  return (
    <div data-testid="program-workspace">
      <button onClick={onBack} className="text-[11px] font-semibold text-[#6B7280] hover:text-[#1F2937] inline-flex items-center gap-1 mb-2" data-testid="workspace-breadcrumb">
        Programs<ChevronRight className="h-3 w-3" /><span className="text-[#1F2937]">{program.name}</span>
      </button>

      <div className="bg-white rounded-2xl border border-[#EFF0F2] p-5 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-[#1F2937] font-['Outfit']" data-testid="workspace-program-name">{program.name}</h2>
              <Badge className={`border-0 text-[10px] ${PROGRAM_PILL[program.status]}`}>
                {PROGRAM_STATUS.find(s => s.value === program.status)?.label}
              </Badge>
              {!['closed', 'draft'].includes(program.stage) && (
                <Badge className={`border-0 text-[10px] ${STAGE_PILL[program.stage]}`} data-testid="workspace-stage">{program.stage_label}</Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#4B5563]">
              <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3 text-[#9CA3AF]" />{formatDayYear(program.start_date)} – {formatDayYear(program.end_date)}</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#9CA3AF]" />Apply by {formatDayYear(program.application_deadline)}</span>
              <span className="flex items-center gap-1"><GraduationCap className="h-3 w-3 text-[#9CA3AF]" />Grades {(program.eligible_grades || []).join(', ')}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {program.action_count > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#F06651] bg-[#FFF0ED] rounded-full px-2.5 py-1" data-testid="workspace-action-count">
                <Bell className="h-3 w-3" />{program.action_count} to action
              </span>
            )}
            <Button size="sm" variant="outline" onClick={onEdit} disabled={!!program.lifecycle?.locked}
              className="rounded-lg text-xs border-gray-200" data-testid="workspace-edit-program">
              <Pencil className="h-3.5 w-3.5 mr-1" />Edit program
            </Button>
            {program.stage === 'accepting_applications' && (
              <Button size="sm" variant="outline" onClick={() => setClosingApps(true)}
                className="rounded-lg text-xs border-gray-200" data-testid="close-applications-btn">
                Close applications
              </Button>
            )}
            {program.applications_closed_at && program.status === 'open'
              && program.application_deadline >= new Date().toISOString().slice(0, 10) && (
              <Button size="sm" variant="outline" onClick={() => setReopeningApps(true)}
                className="rounded-lg text-xs border-gray-200" data-testid="reopen-applications-btn">
                Reopen applications
              </Button>
            )}
            {program.lifecycle?.can_close && (
              <Button size="sm" onClick={() => setClosing(true)} className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-lg text-xs" data-testid="close-program-btn">
                <Lock className="h-3.5 w-3.5 mr-1" />Close program
              </Button>
            )}
          </div>
        </div>
        {program.lifecycle?.lock_reason && (
          <p className="text-[11px] text-[#9CA3AF] mt-2.5 flex items-start gap-1.5" data-testid="workspace-lock-note">
            <Lock className="h-3 w-3 mt-px shrink-0" />{program.lifecycle.lock_reason}
          </p>
        )}
        {program.lifecycle?.end_passed && program.status !== 'closed' && !program.lifecycle.can_close && (
          <div className="mt-2.5 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="closure-pending">
            <p className="text-[11px] font-semibold text-amber-800">Closure pending — still outstanding:</p>
            <ul className="mt-1 space-y-0.5">
              {program.lifecycle.blockers.map(b => (
                <li key={b.kind}>
                  <button onClick={() => setTab(b.tab)} className="text-[11px] text-amber-700 underline" data-testid={`closure-blocker-${b.kind}`}>
                    {b.count} {b.label.toLowerCase()}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {program.lifecycle?.can_close && (
          <p className="text-[11px] text-emerald-700 mt-2.5" data-testid="ready-to-close-note">
            The internships have finished and nothing is outstanding — this programme is ready to close.
          </p>
        )}
        {program.applications_closed_at && program.status !== 'closed' && (
          <p className="text-[11px] text-[#6B7280] mt-2.5" data-testid="applications-closed-note">
            Applications closed early by {program.applications_closed_by} on{' '}
            {formatDayYear(program.applications_closed_at.slice(0, 10))} — published deadline was{' '}
            {formatDayYear(program.application_deadline)}. Reason: {program.applications_closed_reason}
          </p>
        )}
        {program.status === 'closed' && program.lifecycle?.closed_at && (
          <p className="text-[11px] text-[#6B7280] mt-2.5" data-testid="workspace-closed-note">
            Closed by {program.lifecycle.closed_by} on {formatDayYear(program.lifecycle.closed_at.slice(0, 10))}.
            Records, certificates and reports stay available.
          </p>
        )}
      </div>

      <div className="flex gap-1 mb-4 bg-[#F3F4F6] rounded-lg p-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${tab === t.id ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'}`}
            data-testid={`workspace-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div data-testid="program-overview">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 mb-5">
            <Metric label="Companies" value={m.companies} icon={Building2} testId="pm-companies" />
            <Metric label="Opportunities" value={`${m.approved_opportunities}/${m.opportunities}`} icon={Briefcase} testId="pm-opportunities" />
            <Metric label="Applicants" value={m.applicants} icon={Users} testId="pm-applicants" />
            <Metric label="Placed" value={m.placements} icon={CheckCircle2} testId="pm-placements" />
            <Metric label="Completed" value={m.completed} icon={Award} tone="accent" testId="pm-completed" />
          </div>

          <ProgramAttention title="Needs your action" items={mine} setTab={setTab}
            emptyText="Nothing waiting on you in this programme." testId="program-attention" />
          <ProgramAttention title="Waiting on others" items={waiting} setTab={setTab}
            emptyText={null} testId="program-waiting" muted />

        </div>
      )}
      {tab === 'companies' && <CompaniesOffersTab program={program} focus={focus} onChanged={onRefresh} />}
      {tab === 'applications' && <InternshipApplicationsTab programId={program.id} focus={focus} onChanged={onRefresh} />}
      {tab === 'placements' && <InternshipExecutionTab programId={program.id} focus={focus} onChanged={onRefresh} />}
      {closingApps && <CloseApplicationsDialog program={program} onClose={() => setClosingApps(false)}
        onDone={() => { setClosingApps(false); onRefresh(); }} />}
      {closing && <CloseDialog program={program} onClose={() => setClosing(false)} onDone={() => { setClosing(false); onRefresh(); }} />}
      {reopeningApps && <ReopenApplicationsDialog program={program} onClose={() => setReopeningApps(false)}
        onDone={() => { setReopeningApps(false); onRefresh(); }} />}
    </div>
  );
}

function ProgramAttention({ title, items, setTab, emptyText, testId, muted }) {
  if (!items.length && !emptyText) return null;
  return (
    <>
      <h3 className={`text-sm font-bold font-['Outfit'] mb-2 flex items-center gap-1.5 ${muted ? 'text-[#6B7280]' : 'text-[#1F2937]'}`}>
        <Bell className={`h-3.5 w-3.5 ${muted ? 'text-[#9CA3AF]' : 'text-[#F97D6A]'}`} />{title}
      </h3>
      {items.length === 0 ? (
        <p className="bg-white rounded-2xl border border-[#EFF0F2] px-4 py-5 text-sm text-[#9CA3AF] text-center mb-5" data-testid={`${testId}-empty`}>
          {emptyText}
        </p>
      ) : (
        <div className="space-y-2 mb-5" data-testid={testId}>
          {items.map(([kind, count]) => (
            <button key={kind} onClick={() => setTab(attentionMeta(kind).tab)}
              className="w-full bg-white rounded-2xl border border-[#EFF0F2] px-4 py-3 text-left hover:border-[#F97D6A] transition-colors flex items-center justify-between gap-3"
              data-testid={`program-attention-${kind}`}>
              <span className="text-sm text-[#1F2937] flex items-center gap-2">
                <span className={`inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-[11px] font-bold ${muted ? 'bg-[#F3F4F6] text-[#6B7280]' : 'bg-[#FFF0ED] text-[#F06651]'}`}>{count}</span>
                {attentionMeta(kind).label}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-[#9CA3AF]" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function CloseApplicationsDialog({ program, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/coordinator/internship-programs/${program.id}/close-applications`,
        { confirm: true, reason });
      toast.success('Applications closed. You can now allocate places.');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not close applications');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="close-applications-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Close applications early?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#4B5563]">
          Families will no longer be able to apply to <b>{program.name}</b>, and you can start allocating
          places. The published deadline ({formatDayYear(program.application_deadline)}) stays on the
          record, and you can reopen the window again until that date.
        </p>
        <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="Why are applications closing early? (e.g. every eligible family has applied)"
          className="rounded-xl text-sm" data-testid="close-applications-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={saving || reason.trim().length < 5}
            className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold"
            data-testid="confirm-close-applications">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Close applications'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReopenApplicationsDialog({ program, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/coordinator/internship-programs/${program.id}/reopen-applications`,
        { confirm: true, reason });
      toast.success('Applications reopened. Families can apply again.');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reopen applications');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="reopen-applications-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Reopen applications?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#4B5563]">
          Families will be able to apply to <b>{program.name}</b> again, up to the published deadline
          ({formatDayYear(program.application_deadline)}). Places you have already allocated are not
          affected, and the earlier closure stays on the record.
        </p>
        <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="Why are applications reopening? (e.g. a newly enrolled student needs to apply)"
          className="rounded-xl text-sm" data-testid="reopen-applications-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={saving || reason.trim().length < 5}
            className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold"
            data-testid="confirm-reopen-applications">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reopen applications'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseDialog({ program, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const m = program.metrics;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/coordinator/internship-programs/${program.id}/close`);
      toast.success('Programme closed');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not close this programme'); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="close-program-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Close {program.name}?</DialogTitle>
          <DialogDescription className="text-xs">
            Closing stops new offers, applications, allocations and placements. Records, certificates and
            reports stay available, and your name and the date are recorded.
          </DialogDescription>
        </DialogHeader>
        <ul className="text-[11px] text-[#4B5563] space-y-1 bg-[#FAFBFC] rounded-xl px-3 py-2.5" data-testid="close-summary">
          <li>{m.applicants} applicant{m.applicants === 1 ? '' : 's'}</li>
          <li>{m.placements} placement{m.placements === 1 ? '' : 's'}</li>
          <li>{m.completed} completed internship{m.completed === 1 ? '' : 's'}</li>
          <li>{program.lifecycle.blockers.length === 0 ? 'Nothing outstanding' : `${program.lifecycle.blockers.length} item(s) still outstanding`}</li>
        </ul>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold" data-testid="confirm-close-program">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Close program
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
